import { useEffect, useMemo, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useMember } from "@/contexts/memberContext";
import { occursOnDate } from "@/lib/recurrence";
import { getLocationLabel, parseDate } from "@/lib/utils";
import { BookingService } from "@/services/BookingService";
import { orgPartyMemberIds } from "@/utils/orgParty";
import { computeTimeAdjustOptions } from "@/utils/timeAdjustOptions";
import { formatDayHeader } from "../RoomSchedule/Brick/BrickWeekSchedule.utils";
import RoomSwatch from "../RoomSwatch/RoomSwatch";
import type { Booking } from "@/types/Booking";
import type { Location } from "@/types/Location";
import type { Room } from "@/types/Room";
import { endTimeToMinutes } from "@/utils/bookingTime";

const STEP = 30;

const StepButton = ({
  label,
  onClick,
  disabled,
  symbol,
  testid,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  symbol: "−" | "+";
  testid: string;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        aria-label={label}
        data-testid={testid}
        disabled={disabled}
        onClick={onClick}
        className={[
          "w-11 h-11 rounded-full border text-2xl font-semibold leading-none transition-colors flex items-center justify-center",
          disabled
            ? "border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed"
            : "border-gray-300 bg-white text-indigo-600 hover:border-indigo-400 hover:bg-indigo-50",
        ].join(" ")}
      >
        {symbol}
      </button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

const hhmmToMinutes = (hhmm: string): number => {
  const p = hhmm.padStart(4, "0");
  return parseInt(p.slice(0, 2), 10) * 60 + parseInt(p.slice(2, 4), 10);
};
const minutesToHhmm = (m: number): string =>
  `${Math.floor(m / 60)
    .toString()
    .padStart(2, "0")}${(m % 60).toString().padStart(2, "0")}`;
const formatHhmm = (hhmm: string): string => `${hhmm.slice(0, 2)}:${hhmm.slice(2)}`;
const formatDuration = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}hr`;
  return `${h}hr ${m}m`;
};

type TimeAdjusterProps = {
  booking: Booking;
  location: Partial<Location> | Location;
  room: Partial<Room> | Room;
  /** Emits the full candidate, or null when the user is back at the original times. */
  onChange: (next: { startTime: string; endTime: string } | null) => void;
};

const TimeAdjuster = ({ booking, location, room, onChange }: TimeAdjusterProps) => {
  const { member } = useMember();
  // The org's own party — so growing a booking next to another of the org's own bookings is allowed
  // (buffer waived), exactly as against the same member's. Undefined for non-org users.
  const sameOrgMemberIds = useMemo(() => orgPartyMemberIds(member), [member]);
  const [loading, setLoading] = useState(true);
  const [dailyBookings, setDailyBookings] = useState<Booking[]>([]);
  const [start, setStart] = useState(hhmmToMinutes(booking.startTime));
  const [end, setEnd] = useState(endTimeToMinutes(booking.endTime, booking.startTime));

  const bookingDate = useMemo(() => parseDate(booking.date), [booking.date]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!room?.id || !bookingDate) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const bookings = await new BookingService().getWeeklyBookingsForRoom(room.id, bookingDate, 1, location?.id);
      const onDay = bookings.filter((b) => occursOnDate(b, bookingDate));
      if (!cancelled) {
        setDailyBookings(onDay);
        setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [room?.id, location?.id, bookingDate?.getTime()]);

  const candidate = { startTime: minutesToHhmm(start), endTime: minutesToHhmm(end) };
  const availability = useMemo(
    () => computeTimeAdjustOptions(candidate, booking, dailyBookings, STEP, sameOrgMemberIds),
    [candidate.startTime, candidate.endTime, booking, dailyBookings, sameOrgMemberIds],
  );

  const isOriginal = booking.startTime === candidate.startTime && booking.endTime === candidate.endTime;

  const emit = (s: number, e: number) => {
    setStart(s);
    setEnd(e);
    const next = { startTime: minutesToHhmm(s), endTime: minutesToHhmm(e) };
    onChange(next.startTime === booking.startTime && next.endTime === booking.endTime ? null : next);
  };

  const reset = () => {
    setStart(hhmmToMinutes(booking.startTime));
    setEnd(endTimeToMinutes(booking.endTime, booking.startTime));
    onChange(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Spinner className="size-6 mr-2" />
        <span className="text-lg">Loading availability…</span>
      </div>
    );
  }

  const reasonLine =
    availability.reasons.startEarlier ??
    availability.reasons.endLater ??
    availability.reasons.startLater ??
    availability.reasons.endEarlier ??
    null;

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex flex-col gap-6">
        {/* Context: room / location / day */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <RoomSwatch swatch={room.swatch ?? ""} name={room.name ?? ""} />
          <span>{getLocationLabel(location)}</span>
          {bookingDate && (
            <>
              <span aria-hidden>·</span>
              <span>{formatDayHeader(bookingDate)}</span>
            </>
          )}
        </div>

        {/* Steppers: [-] start [+]  –  [-] end [+] */}
        <div className="flex items-center justify-center gap-6 py-2">
          <div className="text-center">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Start</div>
            <div className="flex items-center gap-3">
              <StepButton
                symbol="−"
                label="Start 30 minutes earlier"
                testid="start-earlier"
                disabled={!availability.canStartEarlier}
                onClick={() => emit(start - STEP, end)}
              />
              <span className="text-3xl font-bold tabular-nums text-gray-900 min-w-[88px]" data-testid="start-time">
                {formatHhmm(candidate.startTime)}
              </span>
              <StepButton
                symbol="+"
                label="Start 30 minutes later"
                testid="start-later"
                disabled={!availability.canStartLater}
                onClick={() => emit(start + STEP, end)}
              />
            </div>
          </div>

          <span className="text-3xl text-gray-300 font-light pt-6">–</span>

          <div className="text-center">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">End</div>
            <div className="flex items-center gap-3">
              <StepButton
                symbol="−"
                label="End 30 minutes earlier"
                testid="end-earlier"
                disabled={!availability.canEndEarlier}
                onClick={() => emit(start, end - STEP)}
              />
              <span className="text-3xl font-bold tabular-nums text-gray-900 min-w-[88px]" data-testid="end-time">
                {formatHhmm(candidate.endTime)}
              </span>
              <StepButton
                symbol="+"
                label="End 30 minutes later"
                testid="end-later"
                disabled={!availability.canEndLater}
                onClick={() => emit(start, end + STEP)}
              />
            </div>
          </div>
        </div>

        {/* Live total + reason */}
        <div className="text-center">
          <p className="text-sm text-gray-600" data-testid="adjust-duration">
            {formatDuration(end - start)}
            {!isOriginal && (
              <span className="text-indigo-700 font-medium">
                {" "}
                · was {formatHhmm(booking.startTime.padStart(4, "0"))}–{formatHhmm(booking.endTime.padStart(4, "0"))}
              </span>
            )}
          </p>
          {reasonLine && (
            <p className="text-xs text-gray-400 mt-1" data-testid="adjust-reason">
              {reasonLine}
            </p>
          )}
        </div>

        {/* Reset */}
        {!isOriginal && (
          <button
            type="button"
            onClick={reset}
            data-testid="adjust-reset"
            className="text-sm text-gray-500 underline self-center hover:text-gray-700"
          >
            Reset to original
          </button>
        )}
      </div>
    </TooltipProvider>
  );
};

export default TimeAdjuster;
